import { z } from 'zod';
import { BillingType, CreditType } from '../common/enums';
import { AllCharLabelSchema, UUIDSchema } from '../common/schemas';

/**
 * Shared & Helper Schemas
 */

// A Xero item code alone — prices carry dollar amounts in termPrice/lessonPrice,
// so a code never carries an amount of its own: the price's amounts are its term and lesson prices.
export const DBPriceXeroCodeSchema = z.string().min(1).max(32);

/**
 * DB Schemas
 */

export const DBPricePriceIdSchema = UUIDSchema;
export const DBPriceActiveSchema = z.boolean();
// Trimmed before the length checks, so a label of spaces alone is refused
// rather than stored as one the list and the picker render blank
export const DBPriceLabelSchema = AllCharLabelSchema.trim().min(1).max(128).openapi({
  description: 'Descriptive label of the price',
  example: '12CHEMTERM',
});
export const DBPriceLessonPriceSchema = z.number().min(0).openapi({
  description: 'GST-inclusive price per individually billed lesson',
  example: 80,
});
export const DBPriceLessonsPerTermSchema = z.number().int().min(0).max(100);
export const DBPriceTermPriceSchema = z.number().min(0).openapi({
  description: 'GST-inclusive price per full term',
  example: 760,
});
export const DBPriceTermsSchema = z.number().int().min(1).max(100);
export const DBPriceXeroCodesSchema = z.object({
  [BillingType.Extra]: DBPriceXeroCodeSchema,
  [BillingType.Term]: DBPriceXeroCodeSchema,
  [CreditType.Credit]: DBPriceXeroCodeSchema.optional(),
});

export const DBPriceSchema = z.object({
  priceId: DBPricePriceIdSchema,
  active: DBPriceActiveSchema,
  label: DBPriceLabelSchema,
  lessonPrice: DBPriceLessonPriceSchema,
  lessonsPerTerm: DBPriceLessonsPerTermSchema,
  termPrice: DBPriceTermPriceSchema,
  terms: DBPriceTermsSchema,
  xeroCodes: DBPriceXeroCodesSchema,
});

/**
 * Client Schemas
 */

export const PricePriceIdSchema = DBPricePriceIdSchema;
export const PriceActiveSchema = DBPriceActiveSchema;
export const PriceLabelSchema = DBPriceLabelSchema;
export const PriceLessonPriceSchema = DBPriceLessonPriceSchema;
export const PriceLessonsPerTermSchema = DBPriceLessonsPerTermSchema;
export const PriceTermPriceSchema = DBPriceTermPriceSchema;
export const PriceTermsSchema = DBPriceTermsSchema;
export const PriceXeroCodesSchema = DBPriceXeroCodesSchema;

export const PriceSchema = z.object({
  priceId: PricePriceIdSchema.meta({ title: 'Price ID' }),
  active: PriceActiveSchema.default(true).meta({ title: 'Active' }),
  label: PriceLabelSchema.meta({ title: 'Label' }),
  lessonPrice: PriceLessonPriceSchema.meta({ title: 'Lesson price' }),
  lessonsPerTerm: PriceLessonsPerTermSchema.meta({ title: 'Lessons per term' }),
  termPrice: PriceTermPriceSchema.meta({ title: 'Term price' }),
  terms: PriceTermsSchema.meta({ title: 'Terms' }),
  xeroCodes: PriceXeroCodesSchema.meta({ title: 'Xero codes' }),
});
